"use client";

import {
  Checkbox,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CompactTextArea,
  FieldGroup,
} from "@/components/tools/academic-correspondence/kit";
import {
  ARTICLE_TYPES,
  type ArticleType,
  type CompetingInterests,
  type CoverLetterInput,
  LETTER_SIGN_OFFS,
  type LetterSignOff,
} from "@/lib/tool-kit/correspondence/journal-cover-letter";

/*
 * The manuscript, declaration and signature sections of the Journal Cover Letter
 * Generator's form, with the form's state shape. Every word comes from the widget's
 * strings, typed here as the subset these sections read.
 */

export interface CoverLetterFieldStrings {
  manuscriptSection: string;
  titleLabel: string;
  titlePlaceholder: string;
  articleTypeLabel: string;
  articleTypeOptions: Readonly<Record<ArticleType, string>>;
  otherTypeLabel: string;
  otherTypePlaceholder: string;
  soleAuthorLabel: string;
  declarationsSection: string;
  originalLabel: string;
  notUnderLabel: string;
  approvedLabel: string;
  competingLabel: string;
  competingOptions: Readonly<Record<CompetingInterests, string>>;
  competingTextLabel: string;
  competingTextPlaceholder: string;
  preprintLabel: string;
  preprintPlaceholder: string;
  otherLabel: string;
  otherPlaceholder: string;
  signatureSection: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  affiliationLabel: string;
  affiliationPlaceholder: string;
  signOffLabel: string;
  signOffs: Readonly<Record<LetterSignOff, string>>;
}

export type CoverLetterForm = Omit<CoverLetterInput, "date"> & {
  dateIso: string;
};

export const EMPTY_COVER_LETTER: CoverLetterForm = {
  dateIso: "",
  editorTitle: "dr",
  editorName: "",
  journal: "",
  title: "",
  articleType: "research",
  otherType: "",
  soleAuthor: false,
  findings: "",
  significance: "",
  fit: "",
  suggested: "",
  opposed: "",
  original: true,
  notUnderConsideration: true,
  allApproved: true,
  competing: "none",
  competingText: "",
  preprint: "",
  other: "",
  name: "",
  affiliation: "",
  email: "",
  signOff: "sincerely",
  phrasing: "v1",
};

export type CoverLetterUpdate = <K extends keyof CoverLetterForm>(
  key: K,
  value: CoverLetterForm[K]
) => void;

/** The free-text fields, each bound to the form by its key. */
type TextKey =
  | "editorName"
  | "journal"
  | "title"
  | "otherType"
  | "findings"
  | "significance"
  | "fit"
  | "competingText"
  | "preprint"
  | "other"
  | "suggested"
  | "opposed"
  | "name"
  | "affiliation"
  | "email";

export function bindCoverLetterText(
  form: CoverLetterForm,
  update: CoverLetterUpdate
) {
  return (key: TextKey) => ({
    id: `jcl-${key}`,
    onChange: (value: string) => update(key, value),
    value: form[key],
  });
}

export function ManuscriptFields({
  form,
  update,
  strings,
}: {
  form: CoverLetterForm;
  update: CoverLetterUpdate;
  strings: CoverLetterFieldStrings;
}) {
  const text = bindCoverLetterText(form, update);
  return (
    <FieldGroup title={strings.manuscriptSection}>
      <TextField
        {...text("title")}
        label={strings.titleLabel}
        placeholder={strings.titlePlaceholder}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="jcl-type"
          label={strings.articleTypeLabel}
          onChange={(value: ArticleType) => update("articleType", value)}
          options={ARTICLE_TYPES.map((key) => ({
            value: key,
            label: strings.articleTypeOptions[key],
          }))}
          value={form.articleType}
        />
        {form.articleType === "other" ? (
          <TextField
            {...text("otherType")}
            label={strings.otherTypeLabel}
            placeholder={strings.otherTypePlaceholder}
          />
        ) : null}
      </div>
      <Checkbox
        checked={form.soleAuthor}
        label={strings.soleAuthorLabel}
        onChange={(checked) => update("soleAuthor", checked)}
      />
    </FieldGroup>
  );
}

export function DeclarationFields({
  form,
  update,
  strings,
}: {
  form: CoverLetterForm;
  update: CoverLetterUpdate;
  strings: CoverLetterFieldStrings;
}) {
  const text = bindCoverLetterText(form, update);
  return (
    <FieldGroup title={strings.declarationsSection}>
      <div className="space-y-2">
        <Checkbox
          checked={form.original}
          label={strings.originalLabel}
          onChange={(checked) => update("original", checked)}
        />
        <Checkbox
          checked={form.notUnderConsideration}
          label={strings.notUnderLabel}
          onChange={(checked) => update("notUnderConsideration", checked)}
        />
        {form.soleAuthor ? null : (
          <Checkbox
            checked={form.allApproved}
            label={strings.approvedLabel}
            onChange={(checked) => update("allApproved", checked)}
          />
        )}
      </div>
      <PillGroup
        label={strings.competingLabel}
        name="jcl-competing"
        onChange={(value: CompetingInterests) => update("competing", value)}
        options={[
          { value: "none", label: strings.competingOptions.none },
          { value: "declare", label: strings.competingOptions.declare },
        ]}
        value={form.competing}
      />
      {form.competing === "declare" ? (
        <TextField
          {...text("competingText")}
          label={strings.competingTextLabel}
          placeholder={strings.competingTextPlaceholder}
        />
      ) : null}
      <TextField
        {...text("preprint")}
        label={strings.preprintLabel}
        placeholder={strings.preprintPlaceholder}
      />
      <CompactTextArea
        {...text("other")}
        label={strings.otherLabel}
        placeholder={strings.otherPlaceholder}
        rows={2}
      />
    </FieldGroup>
  );
}

export function SignatureFields({
  form,
  update,
  strings,
}: {
  form: CoverLetterForm;
  update: CoverLetterUpdate;
  strings: CoverLetterFieldStrings;
}) {
  const text = bindCoverLetterText(form, update);
  return (
    <FieldGroup title={strings.signatureSection}>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          {...text("name")}
          label={strings.nameLabel}
          placeholder={strings.namePlaceholder}
        />
        <TextField
          {...text("email")}
          label={strings.emailLabel}
          placeholder={strings.emailPlaceholder}
        />
        <TextField
          {...text("affiliation")}
          label={strings.affiliationLabel}
          placeholder={strings.affiliationPlaceholder}
        />
        <SelectField
          id="jcl-sign-off"
          label={strings.signOffLabel}
          onChange={(value: LetterSignOff) => update("signOff", value)}
          options={LETTER_SIGN_OFFS.map((key) => ({
            value: key,
            label: strings.signOffs[key],
          }))}
          value={form.signOff}
        />
      </div>
    </FieldGroup>
  );
}
